import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInterval } from 'react-use';
import classes from './KTileMap.module.scss';
import { CcsonEventDTO, RecoveredMeeplesDTO } from '../../types/ccson_env';
import { useDispatch, useSelector } from 'react-redux';
import { BestActionDTO, DEFAULT_AGENT_SETTINGS, GameDTO, GameEventDTO } from '@envclient/envcore';
import { FaMinus, FaPlus } from 'react-icons/fa';
import { FiRotateCcw } from 'react-icons/fi';
import MeepleCont from '../ccson/MeepleCont';
import KTileItem from './KTileItem';
import { SelectPosActions } from '../ccson/TileMap';
import AssetCont from './AssetCont';
import {
  KActionDTO,
  KPosition,
  KResourceActionDTO,
  KSubmitActionDTO,
  KTaxActionDTO,
  KTileDTO,
  Place,
  ResourceMove,
  TaxPlacement
} from '@envclient/envengine/src/envs/koenigsburg/KTile';
import KEnv from '@envclient/envengine/src/envs/koenigsburg/KEnv';
import { KPlayerDTO } from '@envclient/envengine/src/envs/koenigsburg/KPlayer';
import { TypeManager } from '@dipscope/type-manager';
import { KResource } from '@envclient/envengine/src/envs/koenigsburg/KResource';
import KNode from '@envclient/envengine/src/envs/koenigsburg/KNode';
import { MeepleDTO, MeepleType } from '@envclient/envengine/src/envs/tilebase/Meeple';
import TurnGuide from './TurnGuide';
import structuredClone from '@ungap/structured-clone';
import Button from '../../components/Button';
import PrismaZoom from '../../components/PrismaZoom';
import { RootState } from '../../reducers/store';
import { useMyPlayer } from '../../hooks/useMyPlayer';
import { consumeGameEvent } from '../../reducers/gameActions';
import KTilePhase, { Positions } from './KTilePhase';
import KResourcePhase from './KResourcePhase';
import KTaxmanPhase from './KTaxmanPhase';
import RecoverMeeples from './RecoverMeeples';
import LastTileCont from './LastTileCont';
// bundled as a separate Web Worker chunk by worker-loader (see worker-loader.d.ts). Inline loader
// syntax keeps the rest of the app CommonJS, avoiding the ESM circular-dep issues.
// eslint-disable-next-line import/no-webpack-loader-syntax
import KAgentWorker from 'worker-loader!./kAgent.worker';

if (!('structuredClone' in globalThis)) {
  globalThis.structuredClone = structuredClone;
}


// size of the client-compute worker pool (tile-phase orderings are split across it)
const WORKER_POOL_SIZE = Math.max(1, Math.min((navigator.hardwareConcurrency || 4) - 1, 16));

// all orderings of the pulled tile ids (<= 3! = 6, or 4! = 24 with an asset). Inlined here so the
// main bundle doesn't pull in the whole search core - the heavy search runs only in the workers.
function tilePermutations<T>(items: T[]): T[][] {
  if (items.length <= 1) return [items];
  const out: T[][] = [];
  for (let i = 0; i < items.length; i++) {
    const rest = [...items.slice(0, i), ...items.slice(i + 1)];
    for (const perm of tilePermutations(rest)) out.push([items[i], ...perm]);
  }
  return out;
}

// Whether to try placing a hexa asset (city) this turn. Mirrors kAgentCore.shouldPlaceAsset -
// inlined so the main bundle doesn't pull in the search core; KEEP THE THRESHOLDS IN SYNC.
const ASSET_MIN_UNCONNECTED = 2; // place a city only once the player has MORE than this many stuck resources
const ASSET_ENDGAME_TILES_LEFT = 3; // ...or once few tiles remain (unused assets score nothing)
function shouldPlaceAsset(env: KEnv): boolean {
  const myId = env.tileLogic.playerList.actPlayer.id;
  let stuck = 0;
  for (const t of env.tileLogic.tileData.values()) {
    const r = t.resource;
    if (r && r.playerId === myId && !r.harvested) stuck++;
  }
  return stuck > ASSET_MIN_UNCONNECTED || env.tileLogic.tilesLeft() <= ASSET_ENDGAME_TILES_LEFT;
}

// group actions by position:  1-N mapping !!
export const positionToString = (p: KPosition) => `${p.x}:${p.y}:${p.slot}`;
export const placeToString = (place: Place) => place.positions.map((p) => positionToString(p)).join('_');
export type { Positions } from './KTilePhase';

// The actual position in viewport coordinates of the element's local (0,0) point (after transforms).
function getTransformedOrigin(el) {
  const style = getComputedStyle(el);
  const transform = style.transform;
  const origin = style.transformOrigin.split(' ').map(parseFloat);

  const rect = el.getBoundingClientRect();

  if (!transform || transform === 'none') {
    return {
      x: rect.left + origin[0],
      y: rect.top + origin[1]
    };
  }

  const matrix = new DOMMatrix(transform);

  // Transform local (0,0) point
  const local = matrix.transformPoint({ x: -origin[0], y: -origin[1] });

  return {
    x: rect.left + origin[0] + local.x,
    y: rect.top + origin[1] + local.y
  };
}

interface KTileMapProps {
  game: GameDTO;
  onSubmit: (action?: KSubmitActionDTO | KResourceActionDTO | KTaxActionDTO) => void;
  active: boolean;
  clone: { [key: string]: unknown };
}

/**
 * KTile lifecycle:
 * - new tiles are in lastTiles - placed on canvas on left side with a tempPos style (they are unplaced tiles - not yet in tileData)
 * - when one is clicked it sets the selectedTileId, becomes "selected" and current actions are calculated for this tile
 * - when a placeholder is clicked - selected tile is added to KTileLogic.tileData - becomes "placed" by setting isTemp:true and the default action is set as selectedAction
 * - further clicks in selectedTile will loop over the actions set by this placeholder (e.g. actions available on this position)
 * - when another temp tile or unplaced is clicked - it becomes the selected tile (can be placed or unplaced)
 */

const KTileMap: FC<KTileMapProps> = (props) => {
  const { active, game, onSubmit, clone } = props;

  const startBtnVisible = game.status === 'IDLE';

  const [submitted, setSubmitted] = useState<boolean>(false);

  const [kEnv, setKEnv] = useState<KEnv | null>(null);
  const [selectedAction, setSelectedAction] = useState<KActionDTO | null>(null);
  const tilesLeft = kEnv?.tileLogic.tilesLeft() || 0;

  const [assetPlaced, setAssetPlaced] = useState<boolean>(false);

  // the exact server-computed bot action for the current phase, applied/selected for display
  // and waiting to be submitted - either by clicking Submit or automatically after the timeout.
  const [bestAction, setBestAction] = useState<KSubmitActionDTO | KResourceActionDTO | KTaxActionDTO | null>(null);
  // bump to force a re-render after mutating kEnv for bot-move visualization
  const [, setVizTick] = useState<number>(0);

  // this client's own seat config. Set via the Agent Settings modal; broadcast in PlayerDTO.
  const savedAgentSettings = useMyPlayer()?.agentSettings;
  const myAgentSettings = savedAgentSettings ?? DEFAULT_AGENT_SETTINGS;
  // agent is opt-in only - off by default (DEFAULT_AGENT_SETTINGS.useAgent) until explicitly enabled.
  const agentEnabled = myAgentSettings.useAgent;
  // pause/resume the agent DURING the game (local runtime toggle; resets to running on reload).
  const [agentPaused, setAgentPaused] = useState<boolean>(false);
  // run the K agent in a browser Web Worker (the public default); nothing computes when off/paused.
  const clientBotCompute = agentEnabled && myAgentSettings.computeOnClient;
  // which K bot: 'mcts' (tree search + heuristic rollout) or 'heuristic' (pool search)
  const mctsAgent = myAgentSettings.agentType === 'mcts';
  // TRUE OVERRIDE: once the human touches the board/controls after the agent proposed, they've taken
  // over this turn - clear the pending bestAction (so the timer stops AND handleSubmit rebuilds from
  // the human-modified temp state) and stop the agent recomputing (ref checked in compute + fresh()).
  const userTookOverRef = useRef<boolean>(false);
  const takeOverFromAgent = () => {
    if (!agentEnabled) return;
    userTookOverRef.current = true;
    setBestAction(null);
  };
  const kWorkersRef = useRef<Worker[]>([]);
  const computingRef = useRef<boolean>(false);
  // bumped on every new server clone; a worker reply computed under an older generation must NOT
  // be applied/submitted (its board has since advanced -> stale action -> "Invalid tile placed").
  const computeGenRef = useRef<number>(0);
  // monotonic id per compute request; the worker echoes it so we apply ONLY the latest post's
  // reply and ignore superseded/stale replies (a slow reply for a prior turn must not be applied).
  const reqIdRef = useRef<number>(0);

  useEffect(() => {
    if (clone) {
      const k = TypeManager.deserialize(KEnv, clone) as KEnv;
      setKEnv(k);
      setSubmitted(false);
      setAssetPlaced(false);
      setBestAction(null);
      userTookOverRef.current = false;
      computingRef.current = false;
      computeGenRef.current += 1;
    }
  }, [clone]);

  const setSelectedActionAndSync = (action: KActionDTO | null) => {
    if (!kEnv) return;
    if (!action) {
      setSelectedAction(null);
      return;
    }
    // a real placement by the user (never the agent - applyBotAction bypasses this) = take over
    takeOverFromAgent();
    const existingTile = kEnv.tileLogic.tileData.getById(action.tile.id);
    if (!existingTile) {
      kEnv.tileLogic.applyTempAction(action);
    } else {
      kEnv.tileLogic.updateTempAction(action);
    }
    setSelectedAction(action);
  };

  const [recoveredMeeplesEvent, setRecoveredMeeples] = useState<RecoveredMeeplesDTO | null>(null);
  const events = useSelector((state: RootState) => state.game.events) as unknown as CcsonEventDTO[];
  const dispatch = useDispatch();
  const [tilePositions, setTilePositions] = useState<Positions>({});
  // mirror of tilePositions for the pointer-up drop handler (avoids stale closures)
  const tilePositionsRef = useRef<Positions>({});
  useEffect(() => {
    tilePositionsRef.current = tilePositions;
  }, [tilePositions]);

  // pixels the pointer must travel before a press becomes a drag (taps keep the click flow)
  const DRAG_THRESHOLD_PX = 6;
  const [dragState, setDragState] = useState<{ tileId: number; pos: { left: number; top: number } } | null>(null);

  const myPlayer: KPlayerDTO = useMyPlayer() as KPlayerDTO;
  const { meeples } = myPlayer;
  const [preferredMeepleType, setPreferredMeepleType] = useState<MeepleType>();

  const [posActions, setPosActions] = useState<KActionDTO[]>([]);

  const [zoom, setZoom] = useState<number>(0.5);

  const canvasRef = useRef<HTMLDivElement>(null);
  const [tileStartStyle, setTileStartStyle] = useState<{ left: number; top: number } | null>(null);

  const [selectedTileId, setSelectedTileId] = useState<number | null>(null);

  // is the active tile between lastTiles
  const selectTileAndCalculateActions = (action: KTileDTO) => {
    setSelectedTileId(action.id);
    console.log('selectTileAndCalculateActions', action);
    const newActions = kEnv.tileLogic.calculateCurrentActions(action);
    groupCurrentActionsByPosition(newActions);
  };

  const groupCurrentActionsByPosition = (newActions: KActionDTO[]) => {
    // clear state
    setSelectedActionAndSync(null);
    setSubmitted(false);
    const tilePositions: Positions = {};
    // one action/tile can map to multiple positions (rhombus, hexa)
    newActions.forEach((a) => {
      const tileDTO = a.tile;
      if (tileDTO.place) {
        tileDTO.place.positions.forEach((p) => {
          const pos = positionToString(p);
          if (!tilePositions[pos]) {
            tilePositions[pos] = { coord: pos, position: p, actions: [] };
          }
          tilePositions[pos].actions.push(a);
        });
      }
    });
    setTilePositions(tilePositions);
  };

  //reset state when action changes

  useEffect(
    function processEvents() {
      if (events?.length === 0) return;
      // process first RECOVERED_MEEPLES event in queue
      const meeplesEvent = events.filter((e) => e.type === 'RECOVERED_MEEPLES')[0] as RecoveredMeeplesDTO;
      if (!meeplesEvent) return;
      setRecoveredMeeples(meeplesEvent);
      dispatch(consumeGameEvent(meeplesEvent as unknown as GameEventDTO));
    },
    [events, dispatch]
  );

  // Apply a bot action (from the server BEST_ACTION event OR local client-side compute) for
  // display, then stash it as the pending action that handleSubmit / the auto-submit timeout sends.
  const applyBotAction = (action: KSubmitActionDTO | KResourceActionDTO | KTaxActionDTO) => {
    if (!kEnv) return;
    const turn = kEnv.tileLogic.turn;
    try {
      if (turn === 'tile') {
        // apply the pulled tiles as temp placements and select the last one, so handleSubmit
        // rebuilds the submit from the temp tiles (same as a human turn).
        const tiles = (action as KSubmitActionDTO).tiles;
        if (tiles) {
          tiles.forEach((t) => {
            // per-tile try/catch: one tile throwing (e.g. an edge-case placement) must not abort the
            // rest of the loop, and - more importantly - must not skip the re-render below, which
            // used to live after this forEach and so never ran if any tile threw. That was the actual
            // "agent's tile doesn't show up until I click something" bug: the mutation already
            // happened in kEnv, it just never got rendered on this turn.
            try {
              const existing = kEnv.tileLogic.tileData.getById(t.id);
              if (existing) {
                kEnv.tileLogic.updateTempAction({ tile: t });
                return;
              }
              // a hexa asset (city) isn't in lastTiles - stage it from the acting player's hand first
              // so it previews (mirrors a manual asset click). Guard on actPlayer/asset presence.
              if (t.shape === 'hexa' && !kEnv.tileLogic.lastTiles.find((lt) => lt.id === t.id)) {
                const actP = kEnv.actPlayer();
                if (!actP || !actP.assets?.some((a) => a.id === t.id)) return;
                kEnv.tileLogic.addAssetTile(t);
              }
              kEnv.tileLogic.applyTempAction({ tile: t });
            } catch (tileErr) {
              console.warn('bot best-action tile apply failed', t, tileErr);
            }
          });
          if (tiles.length > 0) setSelectedAction({ tile: tiles[tiles.length - 1] });
        }
      } else if (turn === 'resource') {
        const resActions = (action as KResourceActionDTO).resources;
        if (resActions) {
          setResources((prev) => {
            const next = structuredClone(prev);
            resActions.forEach((rm) => {
              const move = next.get(rm.resource.id);
              if (!move) return;
              move.selectedNode =
                move.portNodes.find((n: KNode) => n.id === rm.targetNodeId && n.tile.id === rm.targetTileId) ||
                move.portNodes[0];
            });
            return next;
          });
        }
      } else if (turn === 'taxman') {
        const taxActions = (action as KTaxActionDTO).taxNodes;
        if (taxActions) {
          setTaxableNodes((prev) => {
            const next = structuredClone(prev);
            taxActions.forEach((tp) => {
              const placement = next.get(tp.tileId);
              if (!placement) return;
              const node = placement.portNodes.find((n: KNode) => n.id === tp.nodeId) || placement.portNodes[0];
              if (!node) return;
              // attach a display meeple so the taxman is visible during the pause (mirrors
              // handleTaxClick); the real placement still comes from the submitted action
              const meeple = kEnv.actPlayer().getMeepleByType(MeepleType.meeple);
              if (meeple) {
                const dto = meeple.toDTO();
                dto.nodeId = node.id;
                dto.tileId = tp.tileId;
                node.meeple = dto;
              }
              placement.selectedNode = node;
            });
            return next;
          });
        }
      }
    } catch (e) {
      console.warn('bot best-action apply failed', e);
    } finally {
      // force a re-render regardless of any exception above, so whatever DID get mutated into kEnv
      // becomes visible immediately instead of only appearing after an unrelated click happens to
      // trigger the next render.
      setVizTick((v) => v + 1);
    }
    // store the exact action regardless of whether the visual apply/select succeeded
    setBestAction(action);
  };

  // BEST_ACTION (server-compute path): the server agent computed this bot's action. Only the
  // active client acts on it. When this seat computes in-browser (agentSettings.computeOnClient)
  // the client computes its own moves below and the server sends no BEST_ACTION events.
  useEffect(
    function handleBestAction() {
      if (!events || events.length === 0) return;
      const bestActionEvent = (events as unknown as GameEventDTO[]).find((e) => e.type === 'BEST_ACTION') as
        | BestActionDTO
        | undefined;
      if (!bestActionEvent) return;
      dispatch(consumeGameEvent(bestActionEvent as unknown as GameEventDTO));
      if (clientBotCompute) return;
      if (!active || submitted || !kEnv) return;
      applyBotAction(bestActionEvent.bestAction as KSubmitActionDTO | KResourceActionDTO | KTaxActionDTO);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [events, dispatch, active, submitted, kEnv, clientBotCompute]
  );

  // Create/destroy the client-compute Web Worker pool (only when this seat computes in-browser).
  // Tile-phase orderings are split across the pool (mirrors the server's worker_threads pool).
  useEffect(() => {
    if (!clientBotCompute) return;
    const workers = Array.from({ length: WORKER_POOL_SIZE }, () => new KAgentWorker());
    kWorkersRef.current = workers;
    return () => {
      workers.forEach((w) => w.terminate());
      kWorkersRef.current = [];
    };
  }, [clientBotCompute]);

  // Client-compute path: when it's this bot's turn, compute the move off the main thread and
  // apply + auto-submit it like the server-event path. Tile phase fans the orderings out across
  // the pool and merges; resource/taxman are cheap and use one worker.
  useEffect(
    function computeBotActionClientSide() {
      const workers = kWorkersRef.current;
      if (!clientBotCompute || workers.length === 0 || agentPaused) return;
      if (!active || !kEnv || userTookOverRef.current) return; // human took over this turn
      if (bestAction || submitted || computingRef.current || game.status !== 'PLAYING') return;
      // nothing to place yet: the PLAYING flag can arrive before the tiles are pulled (server sends
      // UPDATE_GAME before UPDATE_ENV), and permutations([]) === [[]] would "complete" an empty
      // placement -> an empty { tiles: [] } submit. Skip until the pull lands.
      if (kEnv.tileLogic.turn === 'tile' && kEnv.tileLogic.lastTiles.length === 0) return;
      computingRef.current = true;
      const gen = computeGenRef.current; // board this compute is for
      const reqId = ++reqIdRef.current; // only THIS (latest) request's replies may be applied
      const cloneSnapshot = clone; // raw server clone - the proven worker input (no re-serialize)
      const k = kEnv;
      // still the current, un-superseded compute?
      // also bail if the human has taken over this turn (an in-flight reply must not clobber them)
      const fresh = () => reqIdRef.current === reqId && computeGenRef.current === gen && !userTookOverRef.current;

      // post one job to one worker; resolve with its reply matched by reqId (a superseded reply,
      // reqId mismatch, is ignored - its promise simply never resolves and is dropped).
      const runOnWorker = (worker: Worker, job: { [k: string]: unknown }): Promise<{ [k: string]: any }> =>
        new Promise((resolve) => {
          worker.onmessage = (e: MessageEvent) => {
            if (e.data?.reqId !== reqId) return;
            resolve(e.data);
          };
          worker.postMessage(job);
        });

      // split the given orderings across the pool, score each slice in a worker, return the best
      const runSearch = async (orderings: number[][]) => {
        const seg = Math.ceil(orderings.length / workers.length);
        const jobs: Promise<{ [k: string]: any }>[] = [];
        for (let i = 0; i < workers.length; i++) {
          const subset = orderings.slice(i * seg, (i + 1) * seg);
          if (subset.length)
            jobs.push(
              runOnWorker(workers[i], { env: cloneSnapshot, orders: subset, tuning: myAgentSettings.heuristicTuning, reqId })
            );
        }
        const results = await Promise.all(jobs);
        return results
          .filter((r) => r && r.candidate)
          .reduce<{ score: number; candidate: any; breakdown?: Record<string, number> }>(
            (a, b) => ((b.score ?? -Infinity) > a.score ? (b as typeof a) : a),
            { score: -Infinity, candidate: null }
          );
      };

      (async () => {
        try {
          if (mctsAgent) {
            // MCTS agent computes the whole-phase action in one worker (not split across the pool)
            const res = await runOnWorker(workers[0], {
              env: cloneSnapshot,
              mcts: true,
              tuning: myAgentSettings.mctsTuning,
              reqId
            });
            if (!fresh()) return;
            computingRef.current = false;
            if (res?.action) applyBotAction(res.action as KSubmitActionDTO | KResourceActionDTO | KTaxActionDTO);
            return;
          }
          if (k.tileLogic.turn === 'tile') {
            const pulledIds = k.tileLogic.lastTiles.map((t) => t.id);
            let best = await runSearch(tilePermutations(pulledIds));
            if (!fresh()) return; // a newer clone/compute superseded this one - drop it
            // if warranted, also search placements that INCLUDE a hexa asset (city); the worker's
            // enumerate pulls it from the player's assets into lastTiles when it sees the id.
            const assets = k.tileLogic.playerList.actPlayer?.assets;
            if (assets && assets.length > 0 && shouldPlaceAsset(k)) {
              // asset (city) placed LAST -> permute only the pulled tiles: 6 orderings, not 24
              const assetOrders = tilePermutations(pulledIds).map((p) => [...p, assets[0].id]);
              const withAsset = await runSearch(assetOrders);
              if (!fresh()) return;
              if (withAsset.candidate) best = withAsset;
            }
            if (!best.candidate) {
              // no ordering placed all tiles in the search - inline greedy on one worker
              const fb = await runOnWorker(workers[0], { env: cloneSnapshot, fallback: true, reqId });
              if (!fresh()) return;
              best = { score: 0, candidate: fb?.candidate ?? null };
            }
            computingRef.current = false;
            if (best.candidate) {
              if (best.breakdown) {
                const summary = (best.candidate.tiles ?? []).map((t: KTileDTO) => `#${t.id}`).join(' ');
                console.log(
                  `KoenigsbourgAgent pick (client) — score=${best.score}, tiles=[${summary}], breakdown=${JSON.stringify(
                    best.breakdown
                  )}`
                );
              }
              applyBotAction(best.candidate as KSubmitActionDTO);
            }
          } else {
            // resource / taxman: cheap - one worker computes the whole-phase action
            const res = await runOnWorker(workers[0], { env: cloneSnapshot, reqId });
            if (!fresh()) return;
            computingRef.current = false;
            if (res?.action) applyBotAction(res.action as KResourceActionDTO | KTaxActionDTO);
          }
        } catch (e) {
          computingRef.current = false;
          console.warn('[bot compute] failed', e);
        }
      })();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clientBotCompute, agentPaused, active, kEnv, clone, bestAction, submitted, game.status]
  );

  const tilePlaceholderClick = ({ posActions }: SelectPosActions) => {
    const defaultAction: KActionDTO = posActions[0] as KActionDTO;
    console.log('tile placeHolder click', posActions.length);
    setSelectedActionAndSync(defaultAction as KActionDTO);
    setPosActions(posActions as KActionDTO[]);
  };

  const selectedTileClick = (selectedAction: KActionDTO) => {
    console.log('selectedTileClick', selectedAction);
    setSelectedActionAndSync(selectedAction as KActionDTO);
  };

  const tempTileClick = (tempTile: KTileDTO) => {
    console.log('tempTileClick', tempTile);
    selectTileAndCalculateActions(tempTile);
    setSelectedActionAndSync({ tile: tempTile });
  };

  useEffect(
    // set posActions to match selectedAction
    function syncPosActions() {
      if (!selectedAction) {
        setPosActions([]);
        return;
      }
      // select actions whose place overlaps with selectedAction place
      const posActions = kEnv.tileLogic.currentActions.filter((action) => {
        return kEnv.tileLogic.tileData.placesOverlap(action.tile.place, selectedAction.tile.place);
      });

      setPosActions(posActions);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedAction, kEnv?.tileLogic.currentActions]
  );

  const lastMeepleClick = (meeple: MeepleDTO) => {
    console.log('meepleClick', meeple, posActions.length);
    if (!posActions?.length) return;
    const defaultAction = posActions[0];
    setSelectedActionAndSync(defaultAction as KActionDTO);
  };

  const handleZoomChange = (value: number) => {
    setZoom(value);
  };

  const handleZoomIn = () => {
    setZoom(zoom + 0.1);
  };

  const handleZoomOut = () => {
    setZoom(zoom - 0.1);
  };

  const handlePosChange = (_x: number, _y: number) => {};

  const handleRotate = () => {
    if (!posActions || !selectedAction) return;
    // try to rotate forw
    let nextRotPos = posActions.find((action: KActionDTO) => action.tile?.rot > selectedAction.tile?.rot);
    //try to rotate backw
    if (!nextRotPos) nextRotPos = posActions.find((action: KActionDTO) => action.tile.rot < selectedAction.tile.rot);
    if (nextRotPos) {
      setSelectedActionAndSync(nextRotPos);
    }
  };

  const handleMeepleTypeChange = (type: MeepleType | undefined) => {
    setPreferredMeepleType(type);
  };

  const handleAssetClick = (tileId: number, startStyle?: { left: number; top: number }) => {
    if (kEnv?.tileLogic?.turn !== 'tile' || !active || assetPlaced) return;
    if (myPlayer.assets.length === 0) return;
    console.log('handleAssetClick', tileId);
    const hexaTile = myPlayer.assets.find((t) => t.id === tileId);
    if (!hexaTile) return;
    kEnv.tileLogic.addAssetTile(hexaTile);
    setAssetPlaced(true);
    setTileStartStyle(startStyle ?? null);
    selectTileAndCalculateActions(hexaTile);
  };

  // screen (client) coords -> canvas-local coords (undo PrismaZoom translate + scale)
  const toCanvasLocal = (clientX: number, clientY: number) => {
    const canvasRect = canvasRef.current!.getBoundingClientRect();
    return { x: (clientX - canvasRect.left) / zoom, y: (clientY - canvasRect.top) / zoom };
  };

  // resolve the placeholder under the cursor and place the tile there (reuses the click flow)
  const handleDrop = (clientX: number, clientY: number) => {
    if (!canvasRef.current) return;
    const placeholders = canvasRef.current.querySelectorAll<HTMLElement>('[data-ph-coord]');
    let hitCoord: string | null = null;
    let bestDist = Infinity;
    placeholders.forEach((el) => {
      const r = el.getBoundingClientRect();
      const inside = clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom;
      if (!inside) return;
      const dx = clientX - (r.left + r.width / 2);
      const dy = clientY - (r.top + r.height / 2);
      const dist = dx * dx + dy * dy;
      if (dist < bestDist) {
        bestDist = dist;
        hitCoord = el.getAttribute('data-ph-coord');
      }
    });
    if (!hitCoord) return;
    const entry = tilePositionsRef.current[hitCoord];
    if (!entry) return;
    tilePlaceholderClick({ selectedPos: { x: entry.position.x, y: entry.position.y }, posActions: entry.actions });
  };

  // pointer-down on a draggable tile: show placeholders immediately (like the first click),
  // then track the pointer. A real drag (past threshold) drops onto a placeholder; a tap falls
  // through to the existing click flow.
  const handleTileDragStart = (
    tile: KTileDTO,
    clientX: number,
    clientY: number,
    rect: DOMRect,
    sourceScale: number,
    pointerId: number
  ) => {
    if (!active || kEnv?.tileLogic.turn !== 'tile' || !canvasRef.current) return;
    const canvas = canvasRef.current;

    // selecting a tile that isn't already the active one generates its placeholders
    if (tile.id !== selectedTileId) {
      if (tile.isTemp) {
        tempTileClick(tile);
      } else {
        const origin = toCanvasLocal(rect.left, rect.top);
        setTileStartStyle({ left: origin.x, top: origin.y });
        selectTileAndCalculateActions(tile);
      }
    }

    // where inside the tile the user grabbed, in canvas-local (logical) px.
    // divide by the SOURCE element's scale (canvas tiles = zoom, tray tiles = 1).
    const offset = { x: (clientX - rect.left) / sourceScale, y: (clientY - rect.top) / sourceScale };
    let dragging = false;

    const handleMove = (e: PointerEvent) => {
      if (!dragging) {
        if (Math.hypot(e.clientX - clientX, e.clientY - clientY) < DRAG_THRESHOLD_PX) return;
        dragging = true;
        // Capture on the stable canvas so move/up keep arriving even after the source tile
        // unmounts (replaced by the ghost). Only once a real drag starts, so taps still click.
        try {
          canvas.setPointerCapture(pointerId);
        } catch {
          // ignore - capture is a best-effort robustness improvement
        }
      }
      const l = toCanvasLocal(e.clientX, e.clientY);
      setDragState({ tileId: tile.id, pos: { left: l.x - offset.x, top: l.y - offset.y } });
    };
    const cleanup = () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      window.removeEventListener('pointercancel', handleCancel);
      try {
        canvas.releasePointerCapture(pointerId);
      } catch {
        // ignore - pointer may already be released
      }
      setDragState(null);
    };
    const handleUp = (e: PointerEvent) => {
      if (dragging) handleDrop(e.clientX, e.clientY);
      cleanup();
    };
    const handleCancel = () => cleanup();

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    window.addEventListener('pointercancel', handleCancel);
  };

  const handleSubmit = () => {
    if (!active) return;
    if (submitted) return;
    // bot mode: submit the exact action the agent computed. Works for every phase and lets the
    // Submit button commit immediately instead of waiting for the timeout.
    if (bestAction) {
      // log exactly what is being submitted vs. the current pull, so a stale/mismatched tile
      // action (submitted id not in lastTiles) is obvious in the browser console
      if ('tiles' in bestAction) {
        console.log(
          '[bot submit] tiles=[%s] lastTiles=[%s]',
          (bestAction as KSubmitActionDTO).tiles.map((t) => `${t.id}:${t.shape}`).join(','),
          (kEnv?.tileLogic.lastTiles ?? []).map((t) => `${t.id}:${t.shape}`).join(',')
        );
      }
      onSubmit(bestAction);
      setBestAction(null);
      setSelectedActionAndSync(null);
      setTilePositions({});
      setResources(new Map());
      setTaxableNodes(new Map());
      setSubmitted(true);
      setAssetPlaced(false);
      return;
    }
    if (kEnv.tileLogic.turn === 'tile') {
      if (!selectedAction) return;
      // TODO - check if its contiguous
      // submit temp tiles
      const submitAction: KSubmitActionDTO = {
        tiles: kEnv.tileLogic.tiles().filter((t) => t.isTemp)
        //.map((t) => t.toDTO())
      };
      onSubmit(submitAction);
      setSelectedActionAndSync(null);
      setTilePositions({}); // clear tile positions
    } else if (kEnv.tileLogic.turn === 'resource') {
      if (submitted) return;
      // submit resources
      const resourceAction: KResourceActionDTO = {
        resources: Array.from(resources.values()).map((rm) => ({
          resource: rm.resource,
          targetTileId: rm.selectedNode?.tile.id,
          targetNodeId: rm.selectedNode?.id
        }))
      };
      onSubmit(resourceAction);
      setResources(new Map());
    } else if (kEnv.tileLogic.turn === 'taxman') {
      if (submitted) return;
      // submit tax placments
      const taxAction: KTaxActionDTO = {
        taxNodes: Array.from(taxableNodes.values())
          .filter((tp) => tp.selectedNode)
          .map((tp) => {
            return {
              tileId: tp.cityTileId,
              nodeId: tp.selectedNode.id,
              meepleId: tp.selectedNode.meeple?.id || 0,
              playerId: myPlayer.id
            };
          })
      };
      onSubmit(taxAction);
      setTaxableNodes(new Map());
    }
    setSubmitted(true);
    setAssetPlaced(false);
  };

  const handleSkip = () => {
    if (!active) return;
    if (submitted) return;
    kEnv.tileLogic.removeTempTiles();
    onSubmit(undefined);
    setSelectedActionAndSync(null);
    setSubmitted(true);
    setTilePositions({}); // clear tile positions
    setAssetPlaced(false);
  };

  // auto-submit the agent's pending action after the configured delay. Off when the player disabled
  // auto-submit (they submit manually), took over this turn (bestAction cleared), or paused.
  const autoSubmitArmed = !!bestAction && agentEnabled && myAgentSettings.autoSubmit && !agentPaused;
  // per-second countdown so the Submit button can show "Submitting in Ns"; drives the actual submit
  // (single timer -> label and action stay in sync). Reset whenever the armed state changes.
  const [secsLeft, setSecsLeft] = useState<number | null>(null);
  useEffect(() => {
    setSecsLeft(autoSubmitArmed ? Math.max(1, myAgentSettings.autoSubmitDelay) : null);
  }, [autoSubmitArmed, myAgentSettings.autoSubmitDelay]);
  useInterval(
    () => setSecsLeft((s) => (s == null ? null : Math.max(0, s - 1))),
    autoSubmitArmed && secsLeft !== null && secsLeft > 0 ? 1000 : null
  );
  useEffect(() => {
    if (secsLeft === 0) {
      handleSubmit();
      setSecsLeft(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secsLeft]);

  const tileData = kEnv?.tileLogic.tiles();
  const lastTiles = kEnv?.tileLogic.lastTiles.map((t) => t.toDTO());
  const [resources, setResources] = useState<Map<number, ResourceMove>>(new Map());

  useEffect(() => {
    if (kEnv?.tileLogic.turn !== 'resource') {
      setResources(new Map());
      return;
    }
    if (!active) return;
    // copy harvestable resources
    setResources(kEnv.tileLogic.harvestableResources.map);
  }, [kEnv?.tileLogic.turn, myPlayer.id, active, kEnv?.tileLogic.harvestableResources.map]);

  const handleResourceClick = useCallback(
    (resource: KResource) => {
      const { portNodes, selectedNode } = resources.get(resource.id) || {};
      if (!portNodes || portNodes.length === 0) return;
      // rotate possible port nodes
      const oldIndex = selectedNode ? portNodes.indexOf(selectedNode) : -1;
      const newIndex = oldIndex + 1 < portNodes.length ? oldIndex + 1 : 0;
      resource.moved = true;
      setResources((prev) => {
        const newResources = structuredClone(prev);
        newResources.set(resource.id, { portNodes: portNodes, resource, selectedNode: portNodes[newIndex] });
        return newResources;
      });
    },
    [resources]
  );

  useEffect(() => {
    setTimeout(() => {
      Array.from(resources.values()).forEach((r) => {
        if (!r.selectedNode) handleResourceClick(r.resource);
      });
    }, 1000);
  }, [resources, handleResourceClick]);

  const [taxableNodes, setTaxableNodes] = useState<Map<number, TaxPlacement>>(new Map());

  useEffect(() => {
    if (kEnv?.tileLogic.turn !== 'taxman') {
      setTaxableNodes(new Map());
      return;
    }
    if (!active) return;
    // copy harvestable resources
    setTaxableNodes(kEnv.tileLogic.taxableNodeMap.map);
  }, [kEnv?.tileLogic.turn, active, kEnv?.tileLogic.taxableNodeMap.map]);

  const handleTaxClick = (cityTileId: number, node: KNode) => {
    // check if user has meeple
    const availableMeepleTypes: MeepleType[] = kEnv.actPlayer().availableTypes();
    if (availableMeepleTypes.length === 0) return;

    const { portNodes, selectedNode } = taxableNodes.get(cityTileId) || {};
    // TODO check correctness if multiple taxmans placed
    // TODO remove placed tax meeple
    const meeple = selectedNode
      ? kEnv.actPlayer().getMeepleByType(availableMeepleTypes[0])
      : kEnv.actPlayer().activateMeepleByType(availableMeepleTypes[0]);
    if (!meeple) return;
    node.meeple = meeple;
    meeple.nodeId = node.id;
    meeple.tileId = cityTileId;

    setTaxableNodes((prev) => {
      const newTaxableNodes = structuredClone(prev);
      newTaxableNodes.set(cityTileId, {
        cityTileId: cityTileId,
        portNodes: portNodes,
        selectedNode: node
      });
      return newTaxableNodes;
    });
  };

  const hasCompletedTurn = useMemo(() => {
    if (!kEnv) return false;
    // check if all tiles are placed and all meeples are placed
    if (kEnv.tileLogic.turn === 'tile') {
      return lastTiles?.every((t) => t.isTemp === true);
    } else if (kEnv.tileLogic.turn === 'resource') {
      // check if all resources are moved
      return Array.from(resources.values()).every((rm) => rm.selectedNode !== undefined);
    }
    return true;
  }, [lastTiles, resources, kEnv]);

  const skipBtnEnabled = active && game?.status === 'PLAYING' && kEnv?.tileLogic.turn !== 'resource';
  const submitBtnEnabled = active && game?.status === 'PLAYING' && (hasCompletedTurn || !!bestAction);

  if (!kEnv) return null;

  return (
    <div className={classes.tileMap}>
      <PrismaZoom
        scrollVelocity={0.05}
        currentZoom={zoom}
        onZoomChange={handleZoomChange}
        onPosChange={handlePosChange}>
        <div ref={canvasRef} className={classes.canvas}>
          <div className={classes.bg}></div>
          {tileData.map((t, index) => {
            // dont render temp tiles, they are rendered in KTilePhase
            if (t.isTemp) {
              return null;
            }
            // regular tiles
            return (
              <KTileItem
                active={false}
                key={t.id}
                tile={t}
                last={index === tileData.length - 1}
                onResourceClick={handleResourceClick}
              />
            );
          })}
          {kEnv.tileLogic.turn === 'tile' && (
            <KTilePhase
              active={active}
              lastTiles={lastTiles}
              selectedTileId={selectedTileId}
              selectedAction={selectedAction}
              posActions={posActions}
              preferredMeepleType={preferredMeepleType}
              tilePositions={tilePositions}
              startStyle={tileStartStyle}
              dragTileId={dragState?.tileId ?? null}
              dragPos={dragState?.pos ?? null}
              tilePlaceholderClick={tilePlaceholderClick}
              selectedTileClick={selectedTileClick}
              tempTileClick={tempTileClick}
              lastMeepleClick={lastMeepleClick}
              onTileDragStart={handleTileDragStart}
            />
          )}
          {kEnv.tileLogic.turn === 'resource' && (
            <KResourcePhase
              active={active}
              tileData={tileData}
              resources={resources}
              handleResourceClick={handleResourceClick}
            />
          )}
          {kEnv.tileLogic.turn === 'taxman' && (
            <KTaxmanPhase active={active} taxableNodes={taxableNodes} handleTaxClick={handleTaxClick} />
          )}

          <RecoverMeeples event={recoveredMeeplesEvent} tileData={tileData} onDone={() => setRecoveredMeeples(null)} />
        </div>
      </PrismaZoom>
      <LastTileCont
        lastTiles={lastTiles}
        selectedTileId={selectedTileId}
        active={active}
        canvasRef={canvasRef}
        zoom={zoom}
        onTileClick={(tile, startStyle) => {
          setTileStartStyle(startStyle ?? null);
          selectTileAndCalculateActions(tile);
        }}
        onTileDragStart={handleTileDragStart}
      />
      {active && <TurnGuide turnType={kEnv.tileLogic.turn} />}
      {myPlayer && <MeepleCont color={myPlayer.color} meeples={meeples} onMeepleTypeChange={handleMeepleTypeChange} />}
      {myPlayer && <AssetCont assets={myPlayer.assets} onClick={handleAssetClick} canvasRef={canvasRef} zoom={zoom} />}
      <div className={classes.rightBtnCont}>
        <Button onClick={handleZoomIn} small>
          <FaPlus />
        </Button>
        <Button onClick={handleZoomOut} small>
          <FaMinus />
        </Button>
        <Button onClick={handleRotate} small>
          <FiRotateCcw />
        </Button>
      </div>

      <div className={classes.submitBtn}>
        <Button onClick={handleSubmit} disabled={!submitBtnEnabled}>
          {autoSubmitArmed && secsLeft ? `Submitting in ${secsLeft}s` : 'Submit'}
        </Button>
      </div>
      <div className={classes.skipBtn}>
        <Button onClick={handleSkip} disabled={!skipBtnEnabled}>
          Skip
        </Button>
      </div>
      {agentEnabled && game?.status === 'PLAYING' && (
        <div className={classes.pauseBtn}>
          <Button secondary toggled={agentPaused} onClick={() => setAgentPaused((p) => !p)}>
            {agentPaused ? 'Resume agent' : 'Pause agent'}
          </Button>
        </div>
      )}
      <div className={classes.tilesLeft}>{!startBtnVisible && `Turns: ${tilesLeft}`}</div>
    </div>
  );
};

export default React.memo(KTileMap);
